import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardContent,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle,
  Calendar,
  Clock,
  BookOpen,
} from "lucide-react"
import {
  getPrepTemplates,
  createPrepPlan,
  type PrepTemplateItem,
} from "@/api/examPrep"
import { ROUTES, generatePath } from "@/constants/routes"

export default function NewPlanPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [templates, setTemplates] = useState<PrepTemplateItem[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [selectedTemplate, setSelectedTemplate] = useState<PrepTemplateItem | null>(null)
  const [examDate, setExamDate] = useState("")
  const [dailyMinutes, setDailyMinutes] = useState<number>(0)
  const [competitionYear, setCompetitionYear] = useState<number>(new Date().getFullYear())
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTemplates()
  }, [])

  async function loadTemplates() {
    setLoadingTemplates(true)
    try {
      const data = await getPrepTemplates()
      setTemplates(data ?? [])
    } catch (err) {
      console.error("Failed to load templates:", err)
      setError("加载模板失败，请刷新重试")
    } finally {
      setLoadingTemplates(false)
    }
  }

  function handleSelectTemplate(tpl: PrepTemplateItem) {
    setSelectedTemplate(tpl)
    setDailyMinutes(tpl.dailyMinutes)
  }

  function handleNext() {
    if (step === 1 && !selectedTemplate) return
    setStep(step + 1)
  }

  function handlePrev() {
    if (step > 1) setStep(step - 1)
  }

  async function handleCreate() {
    if (!selectedTemplate) return
    setCreating(true)
    setError(null)
    try {
      const plan = await createPrepPlan({
        templateCode: selectedTemplate.code,
        examDate: examDate || undefined,
        dailyMinutes: dailyMinutes || undefined,
        competitionYear: competitionYear || undefined,
      })
      navigate(generatePath(ROUTES.EXAM_PREP_DETAIL, { planId: plan.id }))
    } catch (err) {
      console.error("Failed to create plan:", err)
      setError("创建计划失败，请稍后重试")
    } finally {
      setCreating(false)
    }
  }

  if (loadingTemplates) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate(ROUTES.EXAM_PREP)}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          返回列表
        </Button>
        <h1 className="text-3xl font-bold text-slate-800">新建备考计划</h1>
        <p className="text-slate-500 mt-1">三步创建你的专属备考方案</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[
          { num: 1, label: "选择考试" },
          { num: 2, label: "配置参数" },
          { num: 3, label: "确认创建" },
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s.num
                  ? "bg-primary text-primary-foreground"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
            </div>
            <span
              className={`text-sm ${
                step >= s.num ? "text-slate-800 font-medium" : "text-slate-400"
              }`}
            >
              {s.label}
            </span>
            {idx < 2 && <div className="w-8 h-px bg-slate-200 mx-1" />}
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-600 text-sm">{error}</div>
      )}

      {/* Step 1: Select Template */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-700 mb-4">选择考试类型</h2>
          {templates.length > 0 ? (
            <div className="grid gap-3">
              {templates.map((tpl) => (
                <Card
                  key={tpl.code}
                  className={`cursor-pointer transition-all ${
                    selectedTemplate?.code === tpl.code
                      ? "ring-2 ring-primary shadow-sm"
                      : "hover:shadow-sm"
                  }`}
                  onClick={() => handleSelectTemplate(tpl)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-800">{tpl.examName}</h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-3.5 h-3.5" />
                            {tpl.grade}
                          </span>
                          <span>{tpl.subject}</span>
                          <span>{tpl.stage}</span>
                        </div>
                      </div>
                      <div className="text-right text-sm text-slate-500">
                        <div>建议 {tpl.defaultTotalWeeks} 周</div>
                        <div>每日 {tpl.dailyMinutes} 分钟</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-slate-500 py-8">暂无可用模板</p>
              </CardContent>
            </Card>
          )}
          <div className="flex justify-end pt-4">
            <Button onClick={handleNext} disabled={!selectedTemplate}>
              下一步
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Configure Parameters */}
      {step === 2 && selectedTemplate && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-slate-700">配置备考参数</h2>
          <Card>
            <CardContent className="pt-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  考试日期
                </label>
                <input
                  type="date"
                  value={examDate}
                  onChange={(e) => setExamDate(e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-slate-400 mt-1">可留空，稍后设置</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  <Clock className="w-4 h-4 inline mr-1" />
                  每日可用时间（分钟）
                </label>
                <input
                  type="number"
                  min={10}
                  max={240}
                  value={dailyMinutes}
                  onChange={(e) => setDailyMinutes(Number(e.target.value))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-slate-400 mt-1">
                  建议 {selectedTemplate.dailyMinutes} 分钟/天
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  竞赛年份
                </label>
                <input
                  type="number"
                  min={2024}
                  max={2030}
                  value={competitionYear}
                  onChange={(e) => setCompetitionYear(Number(e.target.value))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={handlePrev}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              上一步
            </Button>
            <Button onClick={handleNext}>
              下一步
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && selectedTemplate && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-slate-700">确认备考计划</h2>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">考试名称</p>
                  <p className="font-medium text-slate-800">{selectedTemplate.examName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">适用年级</p>
                  <p className="font-medium text-slate-800">{selectedTemplate.grade}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">考试日期</p>
                  <p className="font-medium text-slate-800">
                    {examDate || "待定"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">每日学习时间</p>
                  <p className="font-medium text-slate-800">{dailyMinutes} 分钟</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">建议周期</p>
                  <p className="font-medium text-slate-800">
                    {selectedTemplate.defaultTotalWeeks} 周
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">竞赛年份</p>
                  <p className="font-medium text-slate-800">{competitionYear}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={handlePrev}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              上一步
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  创建中...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  生成备考计划
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
